import React, { useState, useEffect, useCallback, lazy } from 'react';
import { connect } from 'react-redux';
import TabWrapper from './TabWrapper';
import { Box, Typography, Button, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import {
  TextField,
  Grid,
  Alert,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import { ethers } from 'ethers';
import { VestingAbi } from '../abis';
import { getUserCredits, getCreditHistoryByNote } from '../../../../../redux/actions/credit.action';
import {
  getAllUserHasKatAddress,
  getEmailsByWalletAddressesInLPSale,
  sendEmailToDevPartner,
  getVestingAddressesByDaoId,
} from '../../../../../redux/actions/dao.action';
import { inputStyles, buttonStyles } from './css/commonStyles';

const tableStyles = {
  backgroundColor: '#282f38',
  border: '1px solid #00EDC3',
  '& .MuiTableCell-root': {
    color: '#e7e3e3',
    borderColor: '#00EDC3',
  },
  '& .MuiTableHead-root': {
    backgroundColor: '#1a1f24',
  },
};

const Table = lazy(() => import('@mui/material/Table'));

const VestingViewClaimTab = ({
  daoOrganizerAddress,
  chainId,
  currentDao,
  getUserCredits,
  getCreditHistoryByNote,
  getAllUserHasKatAddress,
  getEmailsByWalletAddressesInLPSale,
  sendEmailToDevPartner,
  getVestingAddressesByDaoId,
  vestingAddressesData,
}) => {
  const [addresses, setAddresses] = useState(['']);
  const [allocations, setAllocations] = useState(['']);
  const [currentAddress, setCurrentAddress] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [vestingAddresses, setVestingAddresses] = useState([]); // State to store API data
  const [selectedVestingAddress, setSelectedVestingAddress] = useState('');
  const [releasableAmount, setReleasableAmount] = useState('');
  const vestingList = vestingAddressesData?.data || [];
  const [hasMetaMask, setHasMetaMask] = useState(false);
  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [isClaiming, setIsClaiming] = useState(false);

  const loadReleasableAmount = useCallback(async (address) => {
    if (!address) return;
    try {
      if (!window.ethereum) {
        setError('Please install MetaMask');
        return;
      }
      const selected = vestingAddresses.find((item) => item.vestingAddress === address);
      if (!selected) {
        setError('Vesting contract not found');
        return;
      }
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const contract = new ethers.Contract(selected.vestingAddress, VestingAbi, provider);

      const signer = provider.getSigner();
      const userAddress = await signer.getAddress();

      const amount = await contract.releasable(userAddress);
      setReleasableAmount(Number(amount) / 1e18);
    } catch (err) {
      console.error('Failed to fetch releasable amount: ' + (err?.message || err));
      setReleasableAmount(0);
    }
  }, [vestingAddresses]);

  useEffect(() => {
    setHasMetaMask(typeof window.ethereum !== 'undefined');
    if (window.ethereum) {
      const handleAccountsChanged = async (accounts) => {
        console.log('Account changed:', accounts);
        if (!window.ethereum) {
          setHasMetaMask(false);
          setIsWalletConnected(false);
          setReleasableAmount('');
          return;
        }
        const accountsList = await window.ethereum.request({ method: 'eth_accounts' });
        const isConnected = accountsList.length > 0;
        setIsWalletConnected(isConnected);

        // When wallet changes and a vesting address is selected, reload releasable amount
        if (isConnected && selectedVestingAddress) {
          setReleasableAmount('');
          setSuccess('');
          setError('');
          await loadReleasableAmount(selectedVestingAddress);
        }
        if (!isConnected) {
          setReleasableAmount('');
        }
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      };
    }
  }, [selectedVestingAddress, loadReleasableAmount]);

  const checkWalletConnection = async () => {
    if (!window.ethereum) {
      setHasMetaMask(false);
      setIsWalletConnected(false);
      setReleasableAmount('');
      return;
    }
    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
    const isConnected = accounts.length > 0;
    setIsWalletConnected(isConnected);

    // When wallet changes and a vesting address is selected, reload releasable amount
    if (isConnected && selectedVestingAddress) {
      setReleasableAmount('');
      await loadReleasableAmount(selectedVestingAddress);
    }
    if (!isConnected) {
      setReleasableAmount('');
    }
  };

  useEffect(() => {
    checkWalletConnection();
  }, []);

  const connectWallet = async () => {
    try {
      await window.ethereum.request({ method: 'eth_requestAccounts' });
      checkWalletConnection();
    } catch (err) {
      setError('Failed to connect wallet: ' + (err?.message || err));
    }
  };

  useEffect(() => {
    const fetchVestingAddresses = async () => {
      try {
        const daoId = currentDao?._id; // Lấy daoId từ currentDao
        if (!daoId) {
          setError('DAO ID is missing');
          return;
        }
        const data = await getVestingAddressesByDaoId(daoId); // Gọi API từ props
        console.log('Vesting addresses:', data.data); // Log dữ liệu trả về
        setVestingAddresses(data.data); // Lưu dữ liệu vào state
      } catch (error) {
        console.error('Error fetching vesting addresses:', error);
        setError('Failed to fetch vesting addresses');
      }
    };

    fetchVestingAddresses();
  }, [currentDao, getVestingAddressesByDaoId]);

  const handleChange = async (event) => {
    const address = event.target.value;
    setSelectedVestingAddress(address);
    setReleasableAmount('');
    setError('');
    setSuccess('');
    if (!address) return;
    await loadReleasableAmount(address);
  };

  const handleClaim = async () => {
    if (!selectedVestingAddress || !releasableAmount) return;
    setIsClaiming(true);
    setError('');
    setSuccess('');
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const userAddress = await signer.getAddress();
      const contract = new ethers.Contract(selectedVestingAddress, VestingAbi, signer);
      const tx = await contract.release();
      await tx.wait();
      setSuccess('Claim successful!');

      try {
        await new Promise(resolve => setTimeout(resolve, 1000));
        const amount = await contract.releasable(userAddress);
        setReleasableAmount(Number(amount) / 1e18);
      } catch (refreshErr) {
        console.log('Failed to refresh releasable amount:', refreshErr);
        setReleasableAmount(0);
      }
    } catch (err) {
      setError('Failed to claim: ' + (err?.message || err));
      setSuccess('');
    } finally {
      setIsClaiming(false);
    }
  };

  if (!hasMetaMask) {
    return (
      <Box textAlign="center" p={3}>
        <Typography variant="body1" gutterBottom sx={{ color: '#fcfcfc' }}>
          Please install MetaMask to access this content
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#05e4bc',
            '&:hover': { backgroundColor: '#04c9a5' },
            borderRadius: '12px',
            textTransform: 'none',
            py: 1.5,
          }}
          href="https://metamask.io"
          target="_blank"
          rel="noopener noreferrer"
        >
          Install MetaMask
        </Button>
      </Box>
    );
  }

  if (!isWalletConnected) {
    return (
      <Box textAlign="center" p={3}>
        <Typography variant="body1" gutterBottom sx={{ color: '#fcfcfc' }}>
          Please connect your wallet to access this content
        </Typography>
        <Button
          variant="contained"
          sx={{
            backgroundColor: '#05e4bc',
            '&:hover': { backgroundColor: '#04c9a5' },
            borderRadius: '12px',
            textTransform: 'none',
            py: 1.5,
          }}
          onClick={connectWallet}
        >
          Connect Wallet
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h6" sx={{ mb: 3, pb: 2, color: '#fff', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
        Vesting - View & Claim
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      <Typography variant="subtitle1" sx={{ mb: 2, color: '#00EDC3' }}>
        Select Vesting Address
      </Typography>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel id="vesting-select-label" sx={{ color: '#00EDC3 !important' }}>
          Vesting Contract Address
        </InputLabel>
        <Select
          labelId="vesting-select-label"
          value={selectedVestingAddress}
          label="Vesting Contract Address"
          onChange={handleChange}
          sx={inputStyles}
        >
          {vestingAddresses.map((item) => (
            <MenuItem key={item.vestingAddress} value={item.vestingAddress}>
              {item.name} - {item.vestingAddress}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {selectedVestingAddress && (
        <Typography variant="body2" sx={{ mt: 0.5, color: '#e7e3e3' }}>
          Selected vesting address: {selectedVestingAddress}
        </Typography>
      )}
      {releasableAmount && (
        <Typography sx={{ mt: 2, color: '#00EDC3', fontWeight: 600 }}>Releasable amount: {releasableAmount}</Typography>
      )}
      {releasableAmount && (
        <Button variant="contained" onClick={handleClaim} disabled={isClaiming} sx={{ mt: 2, ...buttonStyles }}>
          {isClaiming ? 'Claiming...' : 'Claim'}
        </Button>
      )}
    </Box>
  );
};

const mapStateToProps = (state) => ({
  currentDao: state.dao?.currentDao || null,
  vestingAddressesData: state.dao?.vestingAddressesData || null,
});

const mapDispatchToProps = {
  getUserCredits,
  getCreditHistoryByNote,
  getAllUserHasKatAddress,
  getEmailsByWalletAddressesInLPSale,
  sendEmailToDevPartner,
  getVestingAddressesByDaoId,
};

export default connect(mapStateToProps, mapDispatchToProps)(VestingViewClaimTab);
